import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import Loading from '../../components/Loading'
import { disable360, enable360, showFullscreen360 } from '../../store/pdp/actions'
import {
  fullscreen360VisibleSelector,
  hasPi20Selector,
  hasPi21Selector,
  hasPicSelector,
} from '../../store/pdp/selectors'
import { breakpoints, palette, zIndex } from '../../style/theme'
import ProductImageFull from '../Product/ProductImageFull'
import Tre60 from '../Tre60'
import RCIcon from '../UI/RCIcon'
import { CatalogImages, CatalogImagesPi } from '../../model/product'
import { getDegrees, getImagesFor360, getProductImageUrl } from '../../libs/productImages'

const Wrapper = styled.div`
  width: 100%;

  .loading-wrapper {
    z-index: ${zIndex.inPageloading};
    background-color: ${palette.white};
  }
`
Wrapper.displayName = 'ImageFullOr360'

const Button360 = styled.button`
  width: 2.8vw;
  height: 2.8vw;
  padding: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: ${breakpoints.XL}) {
    pointer-events: none;
    border: none;
    box-shadow: none;
  }

  img {
    width: 100%;
    height: 100%;
  }
`

const Icon360Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`

type Props = {
  mocoCode: string
  fullscreen: boolean
  className: string
  button360disabled?: boolean
  hasPi20?: boolean
  hasPi21?: boolean
  catalogImages: CatalogImages
}

const getCatalogImagesWithAlternatives = (catalogImages: CatalogImages): CatalogImages => {
  const { pi21, ...rest } = catalogImages

  if (!pi21) return catalogImages

  const pi21WithAlternativeImages: Partial<CatalogImagesPi> = {}
  ;(Object.keys(pi21) as (keyof CatalogImagesPi)[]).forEach(key => {
    const value = pi21[key]
    if (value?.base || value?.shadow) {
      pi21WithAlternativeImages[key] = {
        base: value.base,
        shadow: value.shadow,
      }
    }
  })

  return { ...rest, pi21: pi21WithAlternativeImages as CatalogImagesPi }
}

export const ImageFullOr360: React.FC<Props> = ({
  mocoCode,
  fullscreen,
  className,
  hasPi20,
  hasPi21,
  button360disabled = false,
  catalogImages,
}) => {
  const [loaded, setLoaded] = useState(false)
  const dispatch = useDispatch()
  const colorCode = mocoCode.split('__')[1]
  const hasPi20Selected = useSelector(hasPi20Selector(colorCode))
  const hasPi21Selected = useSelector(hasPi21Selector(colorCode))
  const hasPicSelected = useSelector(hasPicSelector(colorCode))
  const fullscreen360Visible = useSelector(fullscreen360VisibleSelector)
  const hasPi20PropOrSelected = hasPi20 || hasPi20Selected
  const hasPi21PropOrSelected = hasPi21 || hasPi21Selected

  const image360Paths = getDegrees(catalogImages.degrees, fullscreen)
  const imagePi20Paths = getImagesFor360({
    images: catalogImages.pi20,
    fullscreen,
  })
  const imagePi21Paths = getImagesFor360({
    images: catalogImages.pi21,
    fullscreen,
    transitionImageInPlp: fullscreen360Visible,
  })
  const imagePicPaths = getImagesFor360({
    images: catalogImages.pic,
    fullscreen,
  })

  const images = hasPi21PropOrSelected
    ? imagePi21Paths
    : hasPi20PropOrSelected
    ? imagePi20Paths
    : hasPicSelected
    ? imagePicPaths
    : image360Paths

  const hasMultipleImages = images.length > 1
  const catalogImagesWithAlternatives = getCatalogImagesWithAlternatives(catalogImages)

  const shouldRenderSingleImage = !hasMultipleImages

  const onImagesLoad = useCallback(
    (success?: boolean) => {
      setLoaded(true)
      if (success) {
        dispatch(enable360(mocoCode))
      } else {
        dispatch(disable360(mocoCode))
      }
    },
    [setLoaded, dispatch, mocoCode],
  )

  return (
    <Wrapper className={['image-full-or-360', className].join(' ')}>
      {!loaded && <Loading isFullPage={false} />}
      {shouldRenderSingleImage ? (
        <ProductImageFull
          src={getProductImageUrl({ path: catalogImages.base || '' })}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      ) : (
        <Tre60
          key={`${mocoCode}-${fullscreen}-${images.length}`}
          fullscreen={fullscreen}
          images={images}
          onImagesLoadSuccess={() => onImagesLoad(true)}
          onImagesLoadError={() => onImagesLoad()}
        />
      )}
      {hasMultipleImages && (
        <Icon360Wrapper>
          <Button360
            className="icon-360"
            onClick={ev => {
              if (button360disabled) return
              if (fullscreen) {
                ev.preventDefault()
                ev.stopPropagation()
                return
              }
              dispatch(
                showFullscreen360({
                  catalogImages: catalogImagesWithAlternatives,
                  code: mocoCode,
                }),
              )
            }}
          >
            <RCIcon type="360" isGray={false} />
          </Button360>
        </Icon360Wrapper>
      )}
    </Wrapper>
  )
}

export default ImageFullOr360
